import React, { useState } from 'react';
import { clearSavedUrlParams } from '../../utils/urlParams';
import { useCreateCheckoutSessionMutation } from '../../api/checkout';
import LoadingSpiner from '../Loader/LoadingSpiner';
import { getTierDisplayName } from '../../constants/subscriptionTiers';

interface SubscriptionComparisonModalProps {
  currentSubscription: string;
  newTier: string;
  isYearly: boolean;
  isForcedCheckout?: boolean;
  closeModal: () => void;
}

const SubscriptionComparisonModal: React.FC<SubscriptionComparisonModalProps> = ({
  currentSubscription,
  newTier,
  isYearly,
  isForcedCheckout = false,
  closeModal
}) => {
  const [createCheckoutSession, { isLoading: isCheckoutLoading }] = useCreateCheckoutSessionMutation();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleCheckout = async () => {
    try {
      setCheckoutError(null);
      
      const result = await createCheckoutSession({
        tier: newTier,
        isYearly: isYearly
      }).unwrap();
      
      if (result && result.checkout_url) {
        console.log('Redirecting to checkout:', result.checkout_url);
        // Redirect to the Stripe checkout URL
        window.location.href = result.checkout_url;
      } else {
        setCheckoutError('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setCheckoutError('Failed to proceed to checkout. Please try again.');
    }
  };

  // Check if this is a free tier
  const isFreeTier = newTier.toLowerCase() === 'free';
  
  // Check if this is a quota setup scenario (same tier but needs quota assignment)
  const isQuotaSetup = currentSubscription.toLowerCase() === newTier.toLowerCase() && isFreeTier;

  const handleCancel = () => {
    // Clear the URL parameters so they don't trigger again
    clearSavedUrlParams();
    
    closeModal();
  };



  return (
    <div className="container mx-auto">
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isForcedCheckout ? (isFreeTier ? 'Step 1: Account Setup Required' : 'Account Setup Required') : 
             isQuotaSetup ? 'Free Plan Setup' : 'Subscription Change'}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            {isForcedCheckout 
              ? 'You need to set up your account before continuing'
              : isQuotaSetup 
              ? 'Setting up your free plan quotas and storage'
              : 'Review your subscription change details'
            }
          </p>
        </div>

        {/* Subscription Comparison Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Plan Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Current
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  New
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  Plan
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {getTierDisplayName(currentSubscription)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">
                  {getTierDisplayName(newTier)}
                </td>
              </tr>
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  Billing Cycle
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  Current
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {isYearly ? 'Annual' : 'Monthly'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Billing Information Alert */}
        <div className={`alert ${isFreeTier ? 'alert-success' : 'alert-info'} ${isFreeTier ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'}`}>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className={`stroke-current shrink-0 w-6 h-6 ${isFreeTier ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}`}>
            {isFreeTier ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            )}
          </svg>
          <div>
            <h3 className={`font-bold ${isFreeTier ? 'text-green-900 dark:text-green-100' : 'text-blue-900 dark:text-blue-100'}`}>
              {isFreeTier ? 'Free Plan Information' : 'Billing Information'}
            </h3>
            <div className={`text-sm ${isFreeTier ? 'text-green-800 dark:text-green-200' : 'text-blue-800 dark:text-blue-200'}`}>
              {isForcedCheckout ? (
                <span>Setting up your account with {getTierDisplayName(newTier)} plan{isFreeTier ? ' - no payment required' : ''}</span>
              ) : isQuotaSetup ? (
                <span>Setting up quotas for your free plan - no payment required</span>
              ) : isFreeTier ? (
                <span>Switching to the free {getTierDisplayName(newTier)} plan - no payment required</span>
              ) : (
                <>
                  {isYearly ? (
                    <span>Annual billing for {getTierDisplayName(newTier)} plan</span>
                  ) : (
                    <span>Monthly billing for {getTierDisplayName(newTier)} plan</span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {checkoutError && (
          <div className="alert alert-error bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800 dark:text-red-200">{checkoutError}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-4">
          {!isForcedCheckout && (
            <button 
              className="btn btn-outline border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700" 
              onClick={handleCancel}
              disabled={isCheckoutLoading}
            >
              Cancel
            </button>
          )}
          <button 
            className={`btn ${isFreeTier ? 'btn-success bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600' : 'btn-primary bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'} text-white`}
            onClick={handleCheckout}
            disabled={isCheckoutLoading}
          >
            {isCheckoutLoading ? (
              <>
                <LoadingSpiner />
                <span className="ml-2">Processing...</span>
              </>
            ) : (
              isForcedCheckout ? 'Setup Account' :
              isQuotaSetup ? 'Setup Free Plan Quotas' : 
              isFreeTier ? 'Switch to Free Plan' : 'Proceed to Checkout'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionComparisonModal; 